import { useState } from "react";
import { Loader2, PackagePlus } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, fmtDate, fmtDateTime, idemKey, money } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Empty, ErrorBox, Field, PageHead, Spinner, StatusBadge, useFetch } from "@/components/common";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { PayPanel } from "./NewSale";
import { CsvButton } from "./salesUi";

function AssignDialog({ serial, onClose, onDone }) {
  const [f, setF] = useState({ client_name: "", client_email: "", client_mobile: "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setError("");
    try {
      await api.post(`/sales/distributor/cards/${serial}/assign`, { ...f, card_serial: serial, idempotency_key: idemKey() });
      toast.success(`${serial} activated for ${f.client_name}`); onDone();
    } catch (err) { setError(errMsg(err)); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-3xl" data-testid="assign-dialog">
        <DialogHeader><DialogTitle>Assign <span className="font-mono">{serial}</span></DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <Field label="Client full name"><input className="field" value={f.client_name} onChange={set("client_name")} required minLength={2} data-testid="assign-name-input" /></Field>
          <Field label="Client email" hint="The client verifies this email when registering the card"><input type="email" className="field" value={f.client_email} onChange={set("client_email")} required data-testid="assign-email-input" /></Field>
          <Field label="Mobile"><input className="field" value={f.client_mobile} onChange={set("client_mobile")} required minLength={7} data-testid="assign-mobile-input" /></Field>
          <ErrorBox error={error} />
          <button className="btn-primary w-full py-4 text-base" disabled={busy} data-testid="assign-activate-button">{busy && <Loader2 size={16} className="animate-spin" />} Assign & activate</button>
          <p className="text-center text-[11px] text-slate-500">Pre-paid · no payment needed · 12 months start today</p>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function Distributor() {
  const { data, reload } = useFetch("/sales/distributor");
  const [sale, setSale] = useState(null);
  const [assign, setAssign] = useState(null);
  const [busy, setBusy] = useState(false);
  const [key, setKey] = useState(idemKey);
  if (!data) return <Spinner />;
  const pending = data.purchases.find((p) => p.status === "PENDING");
  const buy = async () => {
    if (!window.confirm(`Buy the Distributor Package for ${money(data.price)}? You'll pay by QR Ph.`)) return;
    setBusy(true);
    try { setSale((await api.post("/sales/distributor/purchase", { idempotency_key: key })).data); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const resume = async (id) => { try { setSale((await api.get(`/sales/sales/${id}`)).data); } catch (e) { toast.error(errMsg(e)); } };
  const unassigned = data.cards.filter((c) => c.status === "DISTRIBUTOR_PAID").length;
  return (
    <div className="mx-auto max-w-2xl space-y-5">
      <PageHead eyebrow="Sales Force" title="Distributor Package" sub="Buy 20 pre-paid GIAcare+ Annual memberships and assign them to your own clients." />
      {sale ? (
        <div className="surface space-y-4 p-5" data-testid="distributor-pay-panel">
          <PayPanel sale={sale} onChange={(s) => { setSale(s); if (s.status !== "PENDING") { reload(); setKey(idemKey()); } }} onCancelled={() => { reload(); setKey(idemKey()); }} />
          {sale.status !== "PENDING" && <button className="btn-ghost btn-sm" onClick={() => { setSale(null); reload(); }} data-testid="distributor-back">Back to my cards</button>}
        </div>
      ) : (
        <div className="rounded-3xl bg-gradient-to-br from-violet-700 to-blue-600 p-6 text-white shadow-xl" data-testid="distributor-offer-card">
          <p className="text-xs uppercase tracking-widest opacity-80">Distributor Package</p>
          <p className="mt-1 text-3xl font-extrabold">{data.qty} memberships · {money(data.price)}</p>
          <p className="mt-1 text-sm opacity-90">Serials are issued after PayMongo confirms your payment.</p>
          {pending ? <button className="mt-4 rounded-full bg-white px-5 py-3 font-bold text-purple-700" onClick={() => resume(pending.id)} data-testid="distributor-resume-button">Continue pending payment</button>
            : <button className="mt-4 flex items-center gap-2 rounded-full bg-white px-5 py-3 font-bold text-purple-700 disabled:opacity-60" disabled={busy} onClick={buy} data-testid="buy-distributor-button">{busy ? <Loader2 size={16} className="animate-spin" /> : <PackagePlus size={16} />} Buy Distributor Package</button>}
        </div>
      )}
      <section className="space-y-2" data-testid="my-distributor-cards">
        <p className="label">My Distributor Cards · {unassigned} unassigned of {data.cards.length}</p>
        {!data.cards.length ? <Empty title="No distributor cards yet" /> : data.cards.map((c) => (
          <div key={c.serial} className="surface flex items-center justify-between gap-3 p-3" data-testid={`dist-card-${c.serial}`}>
            <span><b className="font-mono text-sm">{c.serial}</b><br /><span className="text-xs text-slate-500">{c.status === "DISTRIBUTOR_PAID" ? "Paid · Unassigned" : `Assigned to ${c.purchaser?.name || "—"} · ${fmtDate(c.activated_at)}`}</span></span>
            {c.status === "DISTRIBUTOR_PAID" ? <button className="btn-primary btn-sm" onClick={() => setAssign(c.serial)} data-testid={`assign-${c.serial}`}>Assign</button> : <StatusBadge status={c.status} />}
          </div>
        ))}
      </section>
      {assign && <AssignDialog serial={assign} onClose={() => setAssign(null)} onDone={() => { setAssign(null); reload(); }} />}
    </div>
  );
}

export function DistributorAdmin() {
  const { user } = useAuth();
  const { data } = useFetch("/sales/distributor/admin");
  if (!data) return <Spinner />;
  const rows = data.flatMap((p) => p.cards.map((c) => ({ purchase_reference: p.reference, receipt: p.receipt?.number || "", buyer: p.seller_name, paid_at: p.paid_at || "",
    serial: c.serial, status: c.status, assigned_to: c.purchaser?.name || "", activated_at: c.activated_at || "" })));
  return (
    <div className="space-y-4">
      <PageHead eyebrow="Sales Force" title="Distributor Packages" sub={`Viewed by ${user.full_name}. Export serials for card printing.`} actions={<CsvButton name="giacare-distributor-serials.csv" rows={rows} />} />
      {!data.length ? <Empty title="No distributor purchases yet" /> : (
        <div className="surface overflow-x-auto"><table className="data-table" data-testid="distributor-admin-table">
          <thead><tr><th>Buyer</th><th>Reference</th><th>Receipt</th><th>Amount</th><th>Status</th><th>Date</th><th>Assigned</th></tr></thead>
          <tbody>{data.map((p) => (
            <tr key={p.id} data-testid={`dist-purchase-${p.reference}`}><td className="font-semibold">{p.seller_name}</td><td className="font-mono text-xs">{p.reference}</td>
              <td className="font-mono text-xs">{p.receipt?.number || "—"}</td><td>{money(p.amount)}</td><td><StatusBadge status={p.status} /></td>
              <td className="text-xs">{fmtDateTime(p.paid_at || p.created_at)}</td><td className="font-semibold">{p.assigned} / {p.cards.length}</td></tr>
          ))}</tbody>
        </table></div>
      )}
    </div>
  );
}
